/**
 * bk-collection.js  (v2.1.1)
 * --------------------------
 * Strategy change vs v1:
 *
 *   - Pill active state computed in JS by reading URL params
 *     (Liquid logic was fragile — Money values, blank checks).
 *   - Filter changes (pills, drawer apply, back/forward) all flow
 *     through a single URL-change handler, so filter chips/badges
 *     in Boost's facet-filters refresh instantly without reload.
 *   - Pill click no longer fetches directly — just pushState.
 *     The URL-change handler does the AJAX refresh.
 */
(function () {
  'use strict';

  var SECTION_SELECTOR  = '[data-section-type="collection-template"]';
  var BODY_SELECTOR     = '[data-bk-section-body]';
  var GRID_SELECTOR     = '.grid-flex.large-row';
  var LOAD_MORE_ROOT    = '[data-bk-load-more-root]';
  var LOAD_MORE_BUTTON  = '[data-bk-load-more-button]';
  var PILLS_SELECTOR    = '.bk-pills--price';
  var PILL_SELECTOR     = '[data-bk-pill="price"]';

  /* ---------- History patch: emit event on any URL change ---------- */

  (function patchHistory() {
    ['pushState', 'replaceState'].forEach(function (method) {
      var original = history[method];
      history[method] = function () {
        var result = original.apply(this, arguments);
        window.dispatchEvent(new Event('bk:locationchange'));
        return result;
      };
    });
  })();

  /* ---------- Helpers ---------- */

  function getSectionId() {
    var el = document.querySelector(SECTION_SELECTOR);
    return el ? el.getAttribute('data-section-id') : null;
  }

  function fetchSection(url) {
    var sectionId = getSectionId();
    var u = new URL(url, window.location.origin);
    u.searchParams.set('section_id', sectionId);
    return fetch(u.toString(), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.text();
    }).then(function (html) {
      return new DOMParser().parseFromString(html, 'text/html');
    });
  }

  /**
   * Read URL params, compare against each pill's href, mark match
   * with .is-active. Reliable because both sides parse the same way.
   */
  function syncPillActiveStates() {
    var pills = document.querySelectorAll(PILL_SELECTOR);
    if (!pills.length) return;

    var current = new URL(window.location.href).searchParams;
    var currentMin = current.get('filter.v.price.gte') || '';
    var currentMax = current.get('filter.v.price.lte') || '';

    pills.forEach(function (pill) {
      var pillUrl = new URL(pill.getAttribute('href'), window.location.origin);
      var pillMin = pillUrl.searchParams.get('filter.v.price.gte') || '';
      var pillMax = pillUrl.searchParams.get('filter.v.price.lte') || '';
      var match = (pillMin === currentMin) && (pillMax === currentMax);
      pill.classList.toggle('is-active', match);
      pill.setAttribute('aria-selected', match ? 'true' : 'false');
    });
  }

  /**
   * After pills DOM changes, scroll the active pill into the
   * horizontal center of the container.
   */
  function scrollActivePillIntoView(behavior) {
    var pills = document.querySelector(PILLS_SELECTOR);
    if (!pills) return;
    var active = pills.querySelector('.bk-pill.is-active');
    if (!active) return;
    var pillsBox  = pills.getBoundingClientRect();
    var activeBox = active.getBoundingClientRect();
    var delta = (activeBox.left + activeBox.width / 2) - (pillsBox.left + pillsBox.width / 2);
    pills.scrollTo({ left: pills.scrollLeft + delta, behavior: behavior || 'smooth' });
  }

  function clearStickyFocus() {
    var el = document.activeElement;
    if (el && el !== document.body && typeof el.blur === 'function') {
      el.blur();
    }
  }

  /* ---------- Replace strategy ---------- */

  /** Replace the whole filter+grid body. Used on any URL change. */
  function replaceSectionBody(doc) {
    var fresh = doc.querySelector(BODY_SELECTOR);
    var stale = document.querySelector(BODY_SELECTOR);
    if (!fresh || !stale) return false;
    stale.replaceWith(fresh);
    document.dispatchEvent(new CustomEvent('bk:collection:updated', {
      detail: { reason: 'filter-change' }
    }));
    return true;
  }

  /** Append fresh products + replace load-more snippet. Used by Load More. */
  function appendNextPage(doc) {
    var freshGrid = doc.querySelector(GRID_SELECTOR);
    var oldGrid   = document.querySelector(GRID_SELECTOR);
    if (!freshGrid || !oldGrid) return;

    while (freshGrid.children.length) {
      oldGrid.appendChild(freshGrid.children[0]);
    }

    var freshLM = doc.querySelector(LOAD_MORE_ROOT);
    var oldLM   = document.querySelector(LOAD_MORE_ROOT);
    if (freshLM && oldLM) {
      oldLM.replaceWith(freshLM);
    } else if (!freshLM && oldLM) {
      oldLM.remove();
    }

    document.dispatchEvent(new CustomEvent('bk:collection:updated', {
      detail: { reason: 'load-more' }
    }));
  }

  /* ---------- URL change handler ---------- */

  var isUpdating = false;
  var pendingUrl = null;

  function handleLocationChange() {
    var url = window.location.href;
    if (isUpdating) {
      pendingUrl = url;
      return;
    }
    isUpdating = true;
    document.body.classList.add('bk-loading');

    fetchSection(url)
      .then(function (doc) {
        replaceSectionBody(doc);
        syncPillActiveStates();
        scrollActivePillIntoView('smooth');
        clearStickyFocus();
      })
      .catch(function (err) {
        console.error('[bk] location-change fetch failed', err);
      })
      .finally(function () {
        isUpdating = false;
        document.body.classList.remove('bk-loading');
        if (pendingUrl && pendingUrl !== url) {
          pendingUrl = null;
          handleLocationChange();
        } else {
          pendingUrl = null;
        }
      });
  }

  /* ---------- Click handlers ---------- */

  function onPillClick(e) {
    var pill = e.target.closest(PILL_SELECTOR);
    if (!pill) return;
    e.preventDefault();
    var href = pill.getAttribute('href');
    // Optimistic active state — feels instant before fetch resolves
    document.querySelectorAll(PILL_SELECTOR).forEach(function (p) {
      p.classList.toggle('is-active', p === pill);
      p.setAttribute('aria-selected', p === pill ? 'true' : 'false');
    });
    history.pushState({ bkCollection: true }, '', href);
  }

  function onLoadMoreClick(e) {
    var btn = e.target.closest(LOAD_MORE_BUTTON);
    if (!btn) return;
    e.preventDefault();
    var href = btn.getAttribute('href');

    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');

    fetchSection(href)
      .then(function (doc) { appendNextPage(doc); })
      .catch(function (err) {
        console.error('[bk] load-more fetch failed, falling back', err);
        window.location.href = href;
      });
  }

  /* ---------- Pill scrollability hint ---------- */

  function hintPillScroll() {
    var pills = document.querySelector(PILLS_SELECTOR);
    if (!pills || pills.scrollWidth <= pills.clientWidth + 4) return;
    setTimeout(function () {
      pills.scrollTo({ left: 28, behavior: 'smooth' });
      setTimeout(function () { pills.scrollTo({ left: 0, behavior: 'smooth' }); }, 420);
    }, 700);
  }

  /* ---------- Init ---------- */

  function init() {
    document.addEventListener('click', onPillClick);
    document.addEventListener('click', onLoadMoreClick);
    window.addEventListener('bk:locationchange', handleLocationChange);
    window.addEventListener('popstate', handleLocationChange);

    // Sync pill state on initial load (URL might already have filters)
    syncPillActiveStates();
    scrollActivePillIntoView('auto');
    hintPillScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
